/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    EVENT_COMMERCE_REQUEST_START,
    EVENT_COMMERCE_REQUEST_STOP,
    IEventCommerceRequestStopProperties,
    InternalTelemetry,
    StaticTelemetry,
    TelemetryBase
} from '@msdyn365-commerce/telemetry-internal';
import axios, { AxiosRequestConfig } from 'axios';
import { IAny, IGeneric } from '..';
import { ICommerceApiSettings } from '../contract';
import { HTTPMethod, IActionContext, IHTTPError, IHTTPRequestOptions, IHTTPResponse, IODataResponse, IRequestContext } from '../interfaces';

// Default timeout is 10 seconds
const defaultTimeout = 10000;

/**
 * Helper class to call the commerce retail server
 */
export class CommerceRequest {
    private readonly apiSettings: ICommerceApiSettings;
    private readonly requestContext: IRequestContext;

    constructor(context: IActionContext) {
        if (!context) {
            throw Error('Context passed in as null, cannot call API');
        }

        const requestContext = <IRequestContext>(<IGeneric<IAny>>context).requestContext;
        if (!requestContext || !requestContext.apiSettings || !requestContext.apiSettings.baseUrl) {
            throw Error('Context passed is not of type ActionContext, cannot call API');
        }

        this.apiSettings = requestContext.apiSettings;
        this.requestContext = requestContext;
    }

    public post = async <T>(args: { requestUrl: string; body?: unknown; options?: IHTTPRequestOptions }): Promise<IHTTPResponse<T>> => {
        const { httpOptions } = this.parseOptions(args);
        return this.sendCommerceRequest(args.requestUrl, 'post', args.body, httpOptions);
    };

    public get = async <T>(args: {
        requestUrl: string;
        context: IRequestContext;
        options?: IHTTPRequestOptions;
    }): Promise<IHTTPResponse<T>> => {
        const { httpOptions } = this.parseOptions(args);
        return this.sendCommerceRequest(args.requestUrl, 'get', null, httpOptions);
    };

    private parseOptions = (args: { requestUrl: string; body?: unknown; options?: IHTTPRequestOptions }) => {
        const apiSettings = this.apiSettings;
        const { options } = args;
        const requestId =
            (options && options.headers && (options.headers.requestId || options.headers.operationId)) ||
            this.requestContext.operationId ||
            TelemetryBase.generateGuid();
        const httpOptions: IHTTPRequestOptions = {
            ...{ headers: { oun: apiSettings.oun, requestId } },
            ...args.options
        };
        return { httpOptions };
    };

    /**
     * Creates a basic Axios Request Config that is shared across commerce and standard HTTP calls
     * @param requestUrl The url of the request
     * @param httpMethod The HTTP method of the request
     * @param body The body content of the request
     * @param options The options associated with the request
     */
    private createDefaultRequest = (
        requestUrl: string,
        httpMethod?: HTTPMethod,
        body?: unknown,
        options?: IHTTPRequestOptions
    ): AxiosRequestConfig => {
        return {
            method: httpMethod || 'get',
            url: requestUrl,
            data: body,
            timeout: options ? options.timeout || defaultTimeout : defaultTimeout,
            headers: options ? options.headers || {} : {}
        };
    };

    /**
     * Sets retail server specific headers needed for auth
     * @param requestConfig The current configuration settings of the request
     * @param apiSettings The retail server API configuration settings
     */
    private setRetailServerRequestHeaders = (requestConfig: AxiosRequestConfig) => {
        if (!requestConfig.headers) {
            requestConfig.headers = {};
        }

        requestConfig.headers['from-keystone'] = 'true';
        requestConfig.headers.oun = requestConfig.headers.oun || '';
        requestConfig.headers.Accept = requestConfig.headers.Accept || 'application/json;odata=nometadata';
        requestConfig.headers.ContentType = requestConfig.headers.ContentType || 'application/json;odata=verbose';

        return requestConfig;
    };

    /**
     * Parses an OData Response and returns just the return type of the call without the extra OData Metadata
     */
    private parseODataResponse = <T>(response?: IODataResponse<T>): T => {
        if (response && response.value && response['@odata.context']) {
            return response.value;
        } else {
            return <T>(<unknown>response);
        }
    };

    /**
     * Send an retail server request
     * @param requestUrl The url of the request
     * @param httpMethod The HTTP method of the request
     * @param body The body content of the request
     * @param options The options associated with the request
     */
    private sendCommerceRequest = async <T>(
        requestUrl: string,
        httpMethod?: HTTPMethod,
        body?: unknown,
        options?: IHTTPRequestOptions
    ): Promise<IHTTPResponse<T>> => {
        const apiVersion = requestUrl.indexOf('?') === -1 ? '?api-version=7.3' : '&api-version=7.3';
        requestUrl = `${this.apiSettings.baseUrl}${requestUrl}${apiVersion}`;
        let request = this.createDefaultRequest(requestUrl, httpMethod, body, options);
        // generate telemetryid to track event
        const requestId = InternalTelemetry.generateGuid();

        // Add pre-call telemetry here
        StaticTelemetry.trackEvent(EVENT_COMMERCE_REQUEST_START, { id: requestId });

        // Add the retail server request headers
        // Ideally these will be pulled via our zone implementation so they can be completely hidden from the end user
        request = this.setRetailServerRequestHeaders(request);

        // Send the request
        return axios
            .request(request)
            .then((response: IHTTPResponse<IODataResponse<T>>) => {
                // Add any post-call telemetry here
                StaticTelemetry.trackEvent<IEventCommerceRequestStopProperties, {}>(EVENT_COMMERCE_REQUEST_STOP, {
                    statusCode: `${response.status}`,
                    success: 'true',
                    id: requestId
                });

                // Parse OData and return response
                return {
                    ...response,
                    data: this.parseODataResponse(response.data)
                };
            })
            .catch((error: IHTTPError) => {
                StaticTelemetry.trackEvent<IEventCommerceRequestStopProperties, {}>(EVENT_COMMERCE_REQUEST_STOP, {
                    statusCode: `${error.code}`,
                    success: 'false',
                    id: requestId
                });

                // Return either passed error response or empty response to user
                return error.response || <IHTTPResponse>{};
            });
    };
}
